import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminAPI } from '../services/api';
import AdminLayout from '../components/AdminLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Home, Eye, EyeOff, Plus, Users, Download } from 'lucide-react';
import { toast } from 'sonner';

const StatCard = ({ label, value, icon: Icon, accent = 'text-red-500' }) => (
  <Card className="bg-gray-800 border-gray-700">
    <CardContent className="p-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-wide text-gray-400">{label}</p>
          <p className="text-3xl font-bold text-white mt-1">{value}</p>
        </div>
        <Icon className={`w-8 h-8 ${accent}`} />
      </div>
    </CardContent>
  </Card>
);

const AdminDashboard = () => {
  const [stats, setStats] = useState({ total_properties: 0, live_properties: 0, offline_properties: 0, total_leads: 0 });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    adminAPI.stats().then(setStats).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const downloadLeads = async () => {
    setExporting(true);
    try {
      const blob = await adminAPI.exportLeads();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `redpanda-leads-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to download leads CSV');
    } finally {
      setExporting(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Dashboard</h1>
            <p className="text-gray-400 text-sm">Overview of your listings</p>
          </div>
          <Link to="/admin/properties/new">
            <Button className="bg-red-600 hover:bg-red-700" data-testid="dashboard-add-property-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Property
            </Button>
          </Link>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Total Properties" value={loading ? '—' : stats.total_properties} icon={Home} accent="text-red-400" />
          <StatCard label="Live" value={loading ? '—' : stats.live_properties} icon={Eye} accent="text-green-400" />
          <StatCard label="Offline" value={loading ? '—' : stats.offline_properties} icon={EyeOff} accent="text-yellow-400" />
          <StatCard label="Guest Leads" value={loading ? '—' : (stats.total_leads ?? 0)} icon={Users} accent="text-blue-400" />
        </div>

        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h2 className="text-white font-semibold">Guest leads</h2>
                <p className="text-gray-400 text-sm mt-1">
                  Every visitor who verified their phone number to enter the site.
                </p>
              </div>
              <div className="flex gap-2">
                <Link to="/admin/leads">
                  <Button variant="outline" className="border-gray-600 text-gray-200 hover:bg-gray-700" data-testid="dashboard-view-leads-btn">
                    View leads
                  </Button>
                </Link>
                <Button
                  onClick={downloadLeads}
                  disabled={exporting || !stats.total_leads}
                  variant="outline"
                  className="border-gray-600 text-gray-200 hover:bg-gray-700"
                  data-testid="download-leads-csv-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {exporting ? 'Preparing...' : 'Download CSV'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-5">
            <h2 className="text-white font-semibold mb-2">Quick actions</h2>
            <div className="flex flex-wrap gap-3">
              <Link to="/admin/properties">
                <Button variant="outline" className="border-gray-600 text-gray-200 hover:bg-gray-700">
                  Manage properties
                </Button>
              </Link>
              <Link to="/admin/properties/new">
                <Button variant="outline" className="border-gray-600 text-gray-200 hover:bg-gray-700">
                  Add a new listing
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
